import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Heart, Shield, Activity, Leaf, ArrowRight, Check,
  ChevronDown, Clock, Flame, Smile
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LegalFooter from "./legal/LegalFooter";
import PublicNavbar from "./PublicNavbar";

const AdultHomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    if (user) {
      const isAdminOrCoach = user?.role === "admin" || user?.role === "coach";
      const sub = user?.subscription_tier;
      const hasActive = sub && sub !== "free" && sub !== "none" && sub !== "";
      if (isAdminOrCoach || hasActive) navigate("/dashboard");
    }
  }, [user, navigate]);

  const testimonials = [
    {
      name: "Michael Torres",
      role: "Former College Football, Age 42",
      quote: "I feel stronger at 42 than I did at 32. Paladin gave me a system that actually fits my life.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
    },
    {
      name: "Jennifer Lane",
      role: "Working Mom, Age 38",
      quote: "I don't dread working out anymore. The programs are smart, efficient, and I actually see results.",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
    },
    {
      name: "Robert Kim",
      role: "Ex-Swimmer, Age 55",
      quote: "My back doesn't hurt anymore. I can play with my grandkids. That's the real win.",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
    }
  ];

  const pillars = [
    { icon: Heart, title: "LONG-TERM HEALTH", desc: "Programs designed for decades, not weeks. Build a body that stays strong, mobile, and pain-free for life." },
    { icon: Shield, title: "INJURY PREVENTION", desc: "Smart warm-ups, pre-hab routines, and balanced programming that protects your joints and keeps you moving." },
    { icon: Activity, title: "SUSTAINABLE FITNESS", desc: "No burnout. No extreme soreness. Just consistent, enjoyable training that fits around your schedule." },
    { icon: Leaf, title: "NUTRITION FOR LIFE", desc: "Macro guidance tailored to your body, goals, and activity level. Eat well, feel great, no restriction." },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-[#070B14] text-white overflow-x-hidden">
      <PublicNavbar transparent={true} />

      {/* ==================== HERO ==================== */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1517343985841-f8b2d66e010b?w=1920&q=80"
            alt="Strength training"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#070B14]/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-transparent to-[#070B14]/30" />
        </div>

        <div className="relative z-10 w-full max-w-[1800px] mx-auto px-6 md:px-12">
          <motion.div initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }}>
            <p className="text-amber-400 text-sm tracking-[0.3em] uppercase mb-4">For Everyone Who Wants to Feel Great</p>
            <h1 className="font-black text-[10vw] md:text-[8vw] lg:text-[7vw] leading-[0.85] tracking-[-0.02em] uppercase">
              <span className="block">YOUR NEXT</span>
              <span className="block text-amber-400/80">CHAPTER</span>
            </h1>
            <p className="text-white/90 text-base md:text-lg mt-6 max-w-xl">
              Strength training that fits your life. Build a body that lasts — stronger bones, better energy, and the confidence that comes with being in the best shape of your life.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/pricing" className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-black px-8 py-4 rounded-full font-bold tracking-[0.1em] uppercase text-sm hover:from-amber-400 hover:to-amber-500 transition-all shadow-lg shadow-amber-500/25" data-testid="adult-hero-cta">
                Get Started <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/philosophy" className="inline-flex items-center gap-2 border border-white/20 text-white/80 px-8 py-4 rounded-full text-sm tracking-[0.1em] uppercase hover:border-white/40 hover:text-white transition-all">
                Our Approach
              </Link>
            </div>
          </motion.div>
        </div>

        <div className="absolute bottom-12 left-0 right-0 z-10 flex flex-col items-center">
          <span className="text-xs tracking-[0.2em] uppercase text-white/90 mb-3">Scroll to learn more</span>
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.5 }}>
            <ChevronDown className="w-5 h-5 text-white/90" />
          </motion.div>
        </div>
      </section>

      {/* ==================== WHY PALADIN ==================== */}
      <section id="about" className="py-28 px-6 md:px-12 bg-[#070B14]">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <span className="text-amber-400 text-sm tracking-[0.3em] uppercase">01 // Why Paladin</span>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 mb-6 leading-[0.9]">
                TRAINING<br /><span className="text-amber-400/80">THAT LASTS</span>
              </h2>
              <p className="text-white/80 text-base leading-relaxed mb-6">
                Most programs are built for 20-year-olds who can recover from anything. We're different. Paladin is built for people who want to be strong at 40, 50, 60, and beyond.
              </p>
              <p className="text-white/80 text-base leading-relaxed mb-6">
                You don't need to train until you can't walk. You need smart programming that builds you up without breaking you down. That's exactly what we do.
              </p>
              <ul className="space-y-3">
                {[
                  "No extreme soreness or burnout",
                  "Programs adapt to your schedule",
                  "Focus on how you feel, not just numbers",
                  "Expert-designed for long-term results"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-amber-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <img
                src="https://images.unsplash.com/photo-1517343985841-f8b2d66e010b?w=800&q=80"
                alt="Sustainable strength training"
                className="w-full aspect-[4/3] object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#070B14]/40 to-transparent rounded-2xl" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== THE FOUR PILLARS ==================== */}
      <section id="programs" className="py-28 px-6 md:px-12 bg-gradient-to-b from-[#070B14] to-[#0a1020]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
            <span className="text-amber-400 text-sm tracking-[0.3em] uppercase">02 // What You Get</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 leading-[0.9]">
              STRENGTH<br /><span className="text-amber-400/80">FOR LIFE</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:border-amber-500/30 transition-all duration-500" data-testid={`adult-pillar-${i}`}>
                <p.icon className="w-8 h-8 text-amber-400 mb-6" />
                <h3 className="text-lg font-bold tracking-tight mb-3">{p.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section className="py-28 px-6 md:px-12 bg-[#0a1020]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
            <span className="text-amber-400 text-sm tracking-[0.3em] uppercase">03 // How It Works</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 leading-[0.9]">
              SIMPLE.<br /><span className="text-amber-400/80">EFFECTIVE.</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: Clock, step: "01", title: "Pick Your Plan", desc: "Choose a tier that fits your budget. Get instant access to structured programs designed by strength coaches." },
              { icon: Flame, step: "02", title: "Follow the Plan", desc: "Our guided mode walks you through every exercise, every set. It calculates your weights and tells you what to load. Just show up." },
              { icon: Smile, step: "03", title: "Feel the Difference", desc: "More energy. Better sleep. Clothes fit better. After a few weeks, you'll wonder why you didn't start sooner." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15 }}
                className="relative pl-8 border-l-2 border-amber-500/30">
                <item.icon className="w-6 h-6 text-amber-400 mb-4" />
                <span className="text-5xl font-black text-amber-500/25">{item.step}</span>
                <h3 className="text-xl font-bold mt-2 mb-3">{item.title}</h3>
                <p className="text-white/80 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== WHAT MAKES US DIFFERENT ==================== */}
      <section className="py-28 px-6 md:px-12 bg-[#070B14]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-amber-400 text-sm tracking-[0.3em] uppercase">04 // The Difference</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 mb-6 leading-[0.9]">
              PROVEN SYSTEM.<br />GUIDED WORKOUTS.<br /><span className="text-amber-400/80">MEETS YOU WHERE YOU ARE.</span>
            </h2>
            <p className="text-white/80 text-base leading-relaxed">
              We use the same proven, systematic methodology for everyone from 8 to 88. The difference is how it's applied — always tailored to what's most appropriate for you, your body, and your goals.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* What we're NOT */}
            <div className="bg-red-500/5 border border-red-500/20 rounded-2xl p-8">
              <h3 className="text-lg font-bold text-red-400 mb-6">What We're NOT</h3>
              <ul className="space-y-4">
                {[
                  "Random workouts that leave you destroyed",
                  "Guesswork without a real plan",
                  "\"No pain no gain\" mentality",
                  "Unsustainable intensity that burns you out"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                    <span className="text-red-400 mt-0.5">x</span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            {/* What we ARE */}
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-2xl p-8">
              <h3 className="text-lg font-bold text-amber-400 mb-6">What We ARE</h3>
              <ul className="space-y-4">
                {[
                  "A proven system — same methodology, ages 8 to 88",
                  "Tailored to what's appropriate for your body",
                  "Focused on long-term strength and quality of life",
                  "Training that builds you up, never breaks you down"
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70 text-sm">
                    <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <section id="results" className="py-28 px-6 md:px-12 bg-gradient-to-b from-[#070B14] to-[#0a1020]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-16">
            <span className="text-amber-400 text-sm tracking-[0.3em] uppercase">05 // Stories</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mt-6 leading-[0.9]">
              REAL<br /><span className="text-amber-400/80">PEOPLE</span>
            </h2>
          </motion.div>

          <div className="max-w-4xl">
            <AnimatePresence mode="wait">
              <motion.div key={activeTestimonial} initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} transition={{ duration: 0.5 }} className="border-l-2 border-amber-500/50 pl-8 md:pl-12">
                <blockquote className="text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-8">
                  "{testimonials[activeTestimonial].quote}"
                </blockquote>
                <div className="flex items-center gap-4">
                  <img src={testimonials[activeTestimonial].avatar} alt={testimonials[activeTestimonial].name} className="w-14 h-14 object-cover rounded-full" />
                  <div>
                    <p className="font-bold">{testimonials[activeTestimonial].name}</p>
                    <p className="text-white/80 text-sm">{testimonials[activeTestimonial].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
            <div className="flex gap-3 mt-12">
              {testimonials.map((_, i) => (
                <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-1 transition-all ${i === activeTestimonial ? "bg-amber-500 w-12" : "bg-white/20 w-8 hover:bg-white/40"}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="py-28 px-6 md:px-12 bg-[#070B14] relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-transparent to-transparent" />
        <div className="max-w-[1800px] mx-auto relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mb-6 leading-[0.9]">
              YOUR BEST<br />YEARS ARE<br /><span className="text-amber-400">AHEAD</span>
            </h2>
            <p className="text-white/90 text-base mb-10 max-w-xl mx-auto">
              You don't need to be 20 to be strong. Start building the healthiest, most capable version of yourself — right now.
            </p>
            <Link to="/pricing" className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black px-10 py-5 rounded-full font-bold tracking-[0.1em] uppercase text-sm hover:from-amber-400 hover:to-amber-500 transition-all shadow-2xl shadow-amber-500/30" data-testid="adult-cta-bottom">
              Start Your Journey <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
};

export default AdultHomePage;
